import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { User, getEventQueuePermissions, EventQueuePermissions } from '../types/permissions';

/**
 * Хук для получения прав на работу с очередью событий для конкретной фермы
 * CN-0008: Event Queue Permissions
 * 
 * @param farmId - ID фермы
 * @returns Объект с правами на просмотр и управление очередью событий
 * 
 * @example
 * const { canViewMyQueue, canViewAllQueue, canAssignEvents, canCreateEvents } = useEventQueuePermissions(farmId);
 * 
 * if (canViewAllQueue) {
 *   // Показываем вкладки "Мои задачи" и "Очередь событий"
 * } else if (canViewMyQueue) {
 *   // Показываем только "Мои задачи"
 * }
 */
export function useEventQueuePermissions(farmId: string): EventQueuePermissions { // CN-0012: UUID-only
  const user = useSelector((state: any) => state.auth.user) as User | null;
  const isAuthenticated = useSelector((state: any) => Boolean(state.auth.accessToken));
  
  return useMemo(() => {
    const perms = getEventQueuePermissions(user, farmId);
    
    // Fallback: если eventQueue не настроен, показываем страницу "Мои задачи" для всех авторизованных
    if (!perms.canViewMyQueue && !perms.canViewAllQueue && isAuthenticated && farmId) {
      return {
        canViewMyQueue: true,
        canViewAllQueue: false,
        canAssignEvents: false,
        canCreateEvents: false,
      };
    }
    
    return perms;
  }, [user, farmId, isAuthenticated]);
}
